<template>
  <div class="editor-content" v-show="!focused">
    <div v-if="loading" class="spinner-container">
      <div class="spinner-border spinner-light"></div>
    </div>
    <div v-else-if="error && (!hints || !hintFunctions)" class="error">{{ error }}</div>
    <template v-else-if="hints && hintFunctions">
      <div class="editor-content-sidebar">
        <ul>
          <li>
            <button class="tab-btn" :class="{ 'tab-btn-selected': tabPage === 'hints'}"
                    @click="$store.commit('editor/setTabPage', 'hints')"
                    title="Hint statements">
              <SvgIcon icon="hint" :size="20"/>
            </button>
          </li>
          <li>
            <button class="tab-btn" :class="{ 'tab-btn-selected': tabPage === 'sitecomments', 'tab-btn-with-comments': hasComments, 'tab-btn-with-comments-selected': hasComments && tabPage === 'sitecomments' }"
                    @click="$store.commit('editor/setTabPage', 'sitecomments')"
                    title="Site comments">
              <SvgIcon icon="speech-bubble" :size="20"/>
            </button>
          </li>
          <li>
            <button class="tab-btn" :class="{ 'tab-btn-selected': tabPage === 'meta'}"
                    @click="$store.commit('editor/setTabPage', 'meta')"
                    title="Meta tags">
              <SvgIcon icon="table" :size="20"/>
            </button>
          </li>
          <li>
            <button class="tab-btn" :class="{ 'tab-btn-selected': tabPage === 'dates'}"
                    @click="$store.commit('editor/setTabPage', 'dates')"
                    title="Dates">
              <SvgIcon icon="calendar" :size="20"/>
            </button>
          </li>
        </ul>
      </div>

      <div class="editor-content-inner">
        <template v-if="tabPage === 'hints'">
          <template v-if="selectedHint !== null">
            <div class="url-rule" :class="{ error: selectedHint.invalid_url_rule }">
              URL rule:
              <input type="text" disabled :value="selectedHint.url_rule">
            </div>
          </template>

          <!-- TODO: get rid of deprecated html table attributes -->
          <table cellpadding="0" cellspacing="0" class="hint-statements-table" v-if="selectedHint !== null">
            <thead>
            <tr>
              <!-- Give everything but arguments a width to allow it to grow -->
              <th class="th-icon"></th>
              <th class="th-icon"></th>
              <th class="th-short"></th>
              <th class="th-function">Function</th>
              <th>Arguments</th>
              <th class="th-short">
                <div class="flex">
                  <Checkbox id="active-all" type="checkbox" v-model="activeAll" label="Active"/>
<!--                  <input id="active-all" type="checkbox" v-model="activeAll">-->
<!--                  &nbsp;<label for="active-all">Active</label>-->
                </div>
              </th>
              <th class="th-short">Actions</th>
            </tr>
            </thead>
            <draggable v-model="selectedHint.statements" handle=".position-drag" ghost-class="ghost" tag="tbody"
                       id="hint-statements" @update="madeChanges = true">
              <HintStatement v-for="(statement, i) in selectedHint.statements" :key="i" :statement="statement" :i="i"/>
            </draggable>
            <HintStatement :placeholder="true"/>
          </table>
          <span v-else>No hints available for this site.</span>
        </template>
        <section v-else-if="tabPage === 'sitecomments'" class="comment-section">
          <div>
            <section v-if="comments.length > 0" class="comments">
              <h3>Comments</h3>
              <table>
                <tr class="comment-row" v-for="(comment, index) in comments" :key="index">
                  <td class="timestamp">{{ comment.timestamp }} </td>
                  <td><span :style="{ backgroundColor: getUserColor(comment.id_user) }" class="dot"></span></td>
                  <td class="username">{{ comment.username || getDisplayName(comment) || comment.id_user}}:</td>
                  <td><span> {{ comment.comment }}</span></td>
                </tr>
              </table>
            </section>
            <section v-else class="no-comment">
              <span>No comments available for this site.</span>
            </section>

            <section class="submit-comment">
              <h3>Add a Comment</h3>
              <textarea v-model="newComment" rows="2" cols="50" placeholder="Enter your comment"></textarea>
              <button @click="submitComment" class="fancy-button">Submit Comment</button>
            </section>
          </div>
        </section>
        <div v-else-if="tabPage === 'meta'" class="padded">
          <span>Not yet implemented</span>
        </div>
        <div v-else-if="tabPage === 'dates'" class="padded">
          <div v-if="loadingDates" class="spinner-container">
            <div class="spinner-border spinner-light"></div>
          </div>
          <table class="hint-statements-table"
                 v-else-if="Object.keys(dates).length && selectedArticle && dates[selectedArticle.id_link]">
            <thead>
            <tr>
              <th>Link visited</th>
              <th># date</th>
              <th>Picked as article date</th>
              <th>Date location</th>
              <th>Date order</th>
              <th>
                <span content="The raw date we found in the article" v-tippy>Raw date string</span>
              </th>
              <th>Final date</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(date, i) in dates[selectedArticle.id_link]" :key="'date-' + i"
                :class="{ inactive: date.picked !== 1 }">
              <td><span :title="date.visited|formatRelative">{{ date.visited|formatDate }}</span></td>
              <td>{{ date.nth_date }}</td>
              <td>{{ date.picked === 1 ? 'Yes' : 'No' }}</td>
              <td>{{ date.date_location }}</td>
              <td>{{ date.date_order }}</td>
              <td>{{ date.raw_date }}</td>
              <td><span :title="date.parsed_date|formatRelative">{{ date.parsed_date|formatDate }}</span></td>
            </tr>
            </tbody>
          </table>
          <span v-else>No dates available for this article</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SvgIcon from '../SvgIcon'
import HintStatement from '../HintStatement'
import draggable from 'vuedraggable'
import { mapState } from 'vuex'

import { debug } from '../../util/log'
import Checkbox from '../form/Checkbox'
import axios from 'axios'

export default {
  name: 'EditorContent',
  components: { Checkbox, HintStatement, SvgIcon, draggable },
  data() {
    return {
      comments: [],
      newComment: '',
      activeAll: false,
      indiateam: [],
      userMappings: {},
    }
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    hints: {
      required: true
    },
    error: String
  },
  watch: {
    activeAll(val) {
      if (!this.selectedHint || !this.selectedHint.statements) {
        return
      }

      for (let i = 0; i < this.selectedHint.statements.length; i++) {
        this.selectedHint.statements[i].active = val
      }
    },
    tabPage: {
      immediate: true,
      handler(val) {
        if (!this.selectedArticle) {
          return
        }
        if (val === 'dates') {
          this.$store.dispatch('editor/loadArticleDates')
          // this.loadArticleDates()
        }
      }
    },
    selectedArticle: {
      immediate: false,
      handler(val) {
        if (this.tabPage !== 'dates' || !val) {
          return
        }
        debug('EditorContent calling loadArticleDates...')
        this.$store.dispatch('editor/loadArticleDates')
      }
    }
  },
  computed: {
    ...mapState('hint', {
      hintFunctions: state => state.hintFunctions,
      selectedHint: state => state.selected,
    }),
    ...mapState('article', {
      selectedArticle: state => state.selected,
    }),
    ...mapState('editor', {
      tabPage: state => state.tabPage,
      focused: state => state.focused,
      loadingDates: state => state.loadingDates,
      dates: state => state.dates,
    }),
    ...mapState({
      id_site: state => state.id_site,
      id_link: state => state.id_link,
    }),
    username() {
      if (this.$store.getters['auth/isLoggedIn'] && 'username' in this.$store.state.auth.auth.state.user) {
        return this.$store.state.auth.auth.state.user.username
      }
      return 'Unknown'
    },
    id_user() {
      if (this.$store.getters['auth/isLoggedIn'] && 'user_id' in this.$store.state.auth.auth.state.user) {
        return this.$store.state.auth.auth.state.user.user_id
      }
      return 'Unknown'
    },
    hasComments() {
      return this.comments.length > 0
    },
  },
  methods: {
    async fetchComments() {
      try {
        const url = `/api/site-comments?id_site=${this.id_site}`
        console.log('API URL:', url)
        const response = await axios.get(url)
        console.log(response.status)

        if (response.status === 200) {
          console.log(response.data.comments)
          this.comments = response.data.comments
            .filter(comment => comment.comment !== "") // Filter out empty comments
            .map(comment => ({
              ...comment,
              timestamp: comment.timestamp.slice(0, -3) // Remove last 3 characters (seconds)
            }))
          console.log(this.comments)
        } else {
          console.error('Error fetching comments:', response.data.error)
        }
      } catch (error) {
        console.error('Network error:', error)
      }
    },
    async fetchIndiateam() {
      try {
        const url = `/api/india-team`
        console.log('API URL:', url)
        console.log('India status after next')
        const response = await axios.get(url)
        console.log('India status below here') // never reaches this part, TODO: fix api
        console.log(response.status)
        console.log('India status above here')

        if (response.status === 200) {
          this.indiateam = response.data.indiateam

          // Populate the userMappings object
          this.userMappings = this.indiateam.reduce((mappings, user) => {
            mappings[user.id_user] = {
              name: user.name,
              color: user.tag_color,
            }
            return mappings
          }, {})
        } else {
          console.error('Error fetching indiateam: ', response.data.error)
        }
      } catch (error) {
        console.error('Network error:', error)
      }
    },
    async submitComment() {
      try {
        const url = `/api/create-comment`
        const id_user = this.id_user
        const data = {
          id_site: this.id_site,
          comment: this.newComment,
          id_user: id_user,
        }

        const response = await axios.post(url, data)
        console.log('Comment created:', response.data)

        // Clear the comment input field and update the displayed comments
        this.newComment = ''
        this.fetchComments()

      } catch (error) {
        console.error('Error creating comment:', error)
      }
    },
    getUserColor(idUser) {
      const userMapping = this.userMappings[idUser]
      return userMapping ? userMapping.color : 'goldenrod' // Default to goldenrod if not found
    },
    getDisplayName(comment) {
      const userMapping = this.userMappings[comment.id_user]
      if (userMapping) {
        if (userMapping.name) {
          return userMapping.name
        }
      }
    },
  },
  created() {
    this.fetchComments()
    this.fetchIndiateam()
  },
}
</script>

<style lang="sass" scoped>
@use '../../styles/variables' as v

.editor-content
  overflow: hidden
  color: v.$white
  display: flex

  .editor-content-sidebar
    border-right: v.$ui-border

    .tab-btn
      background: none
      border: none
      fill: v.$gray3
      padding: 10px

      &:focus
        outline: none

      &:hover
        background: v.$light-black3

      &.tab-btn-selected
        fill: v.$white

      &.tab-btn-with-comments 
        fill: #dbd171

      &.tab-btn-with-comments-selected
        fill: #ffdd57
  .editor-content-inner
    padding-bottom: 15px
    flex-grow: 1
    overflow: auto

  .hint-statements-table
    width: 100%
    margin-bottom: 30px
    table-layout: fixed

    th, td
      padding: 0 5px

      &:last-child
        text-align: right

    th
      font-size: v.$font-size-smaller
      font-weight: v.$font-weight-semibold

      &.th-icon
        width: 30px

      &.th-short
        width: 80px

      &.th-function
        text-align: right
        width: 300px

  a,
  .btn.btn-link
    color: v.$blue
    cursor: pointer
    padding: 0
    margin: 0
    border: none
    background: none

    &:hover
      color: v.$light-blue

  .url-rule
    padding-left: 10px
    margin-bottom: 2px
    display: flex
    align-items: center
    border-bottom: v.$ui-border

    &.error
      padding: 1px 0

      input
        border: 2px dotted v.$red

    input
      min-width: 700px
      padding: 2px 4px
      margin-left: 4px
      background: none
      border: none
      color: v.$light-gray
      font-family: v.$font-family-monospace
      flex-grow: 1

.comment-section
  width: 100%

.comments
  float: left
  width: 55%
  padding: 20px

.comment-row > td
  vertical-align: top

.timestamp
  white-space: nowrap

.no-comment
  float: left
  width: 55%
  padding: 20px
  
.submit-comment
  padding: 20px
  color: white
  float: left

/* Style for the displayed comments */
.comments ul
  list-style: none
  padding: 0

.comments ul li
  margin-bottom: 10px // Add spacing between comments

.username
  font-weight: bold // Style the username
  text-align: right
  padding-right: 5px

.no-comments
  font-style: italic
  color: #999 // Style for no comments message

/* Style for the Submit Comment button */
.submit-comment button
  display: block
  margin-top: 10px
  color: white

.dot
  width: 1em
  height: 1em
  border-radius: 50%
  display: inline-block
  margin-right: 5px

.fancy-button
  font-size: 1rem
  padding: 0.5em 2em
  background-color: #111
  border: lightgray 1px solid
  color:white
  transition: all 200ms ease
  border-radius: 1px
  outline: none

.fancy-button:hover
  text-decoration: underline

.fancy-button:active, .fancy-button:focus, .fancy-button:focus-within, .fancy-button:target
  background-color: #222
  text-decoration: underline
</style>
